import * as Sentry from '@sentry/react';
import { isFeatureEnabled } from '@/config/featureFlags';

let initialized = false;

/**
 * Telemetry configuration
 * Set VITE_SENTRY_DSN in your .env file for production
 */
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || '';
const ENVIRONMENT = import.meta.env.MODE || 'development';
const RELEASE = import.meta.env.VITE_APP_VERSION || '0.1.0';

/**
 * Initialize telemetry/monitoring services
 * Called once at app startup
 */
export function initTelemetry() {
  if (!isFeatureEnabled('enableTelemetry')) {
    return null;
  }

  if (initialized) {
    return Sentry;
  }

  // Only initialize Sentry in production or if DSN is explicitly provided
  if (SENTRY_DSN) {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: ENVIRONMENT,
      release: RELEASE,

      // Performance monitoring
      tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,

      // Session replay for error reproduction (production only)
      replaysSessionSampleRate: ENVIRONMENT === 'production' ? 0.1 : 0,
      replaysOnErrorSampleRate: ENVIRONMENT === 'production' ? 1.0 : 0,

      // Filter out noisy errors
      ignoreErrors: [
        // Browser extensions
        /extensions\//i,
        /^chrome-extension:\/\//,
        // Network errors that are expected
        'Network Error',
        'Request aborted',
        // ResizeObserver (common false positive)
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
      ],

      // Add additional context
      beforeSend(event, hint) {
        // Don't send events in development unless DSN is explicitly set
        if (ENVIRONMENT === 'development' && !SENTRY_DSN) {
          console.error('[telemetry] Error captured:', hint.originalException);
          return null;
        }
        return event;
      },

      // Integrations
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
    });

    initialized = true;
    console.info('[telemetry] Sentry initialized for', ENVIRONMENT);
  } else if (ENVIRONMENT === 'development') {
    // Development fallback - console logging
    initialized = true;
    console.info('[telemetry] Running in development mode (console logging)');
  }

  return initialized ? Sentry : null;
}

/**
 * Capture an exception with context
 * @param {Error} error - The error to capture
 * @param {Object} context - Additional context
 */
export function captureException(error, context = {}) {
  if (!isFeatureEnabled('enableTelemetry')) return;

  if (SENTRY_DSN && initialized) {
    Sentry.captureException(error, {
      extra: context.extra,
      tags: context.tags,
      contexts: context.contexts,
    });
  } else if (ENVIRONMENT === 'development') {
    console.error('[telemetry] Exception:', error, context);
  }
}

/**
 * Capture a message with context
 * @param {string} message - The message to capture
 * @param {Object} context - Additional context
 */
export function captureMessage(message, context = {}) {
  if (!isFeatureEnabled('enableTelemetry')) return;

  if (SENTRY_DSN && initialized) {
    Sentry.captureMessage(message, {
      level: context.level || 'info',
      extra: context.extra,
      tags: context.tags,
    });
  } else if (ENVIRONMENT === 'development') {
    console.log('[telemetry] Message:', message, context);
  }
}

/**
 * Capture error from React Error Boundary
 * @param {Error} error - The error
 * @param {Object} info - React error info with componentStack
 * @param {string} boundaryName - Name of the error boundary
 */
export function captureBoundaryError(
  error,
  info,
  boundaryName = 'unknown-boundary',
) {
  if (!isFeatureEnabled('enableTelemetry')) return;

  const context = {
    tags: {
      boundary: boundaryName,
      errorType: 'react-boundary',
    },
    contexts: {
      react: {
        boundary: boundaryName,
        componentStack: info?.componentStack,
      },
    },
  };

  if (SENTRY_DSN && initialized) {
    Sentry.captureException(error, context);
  } else if (ENVIRONMENT === 'development') {
    console.error('[telemetry] Boundary Error:', {
      error,
      boundaryName,
      componentStack: info?.componentStack,
    });
  }
}

/**
 * Capture network/API errors
 * @param {Object} appError - Normalized app error
 * @param {Object} context - Additional context (route, feature)
 */
export function captureNetworkError(appError, context = {}) {
  if (!isFeatureEnabled('enableTelemetry')) return;

  const errorContext = {
    level: 'warning',
    tags: {
      code: appError.code,
      category: appError.category,
      route: context.route,
      feature: context.feature,
      errorType: 'network',
    },
    extra: {
      message: appError.message,
      requestId: appError.requestId,
      status: appError.status,
      uxHint: appError.uxHint,
    },
  };

  if (SENTRY_DSN && initialized) {
    Sentry.captureMessage(`Network Error: ${appError.code}`, errorContext);
  } else if (ENVIRONMENT === 'development') {
    console.warn('[telemetry] Network Error:', appError, context);
  }
}

/**
 * Set user context for error tracking
 * @param {Object} user - User object with id, email, etc.
 */
export function setUser(user) {
  if (!isFeatureEnabled('enableTelemetry')) return;

  if (SENTRY_DSN && initialized) {
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.username,
        role: user.role,
      });
    } else {
      Sentry.setUser(null);
    }
  }
}

/**
 * Add breadcrumb for debugging
 * @param {Object} breadcrumb - Breadcrumb data
 */
export function addBreadcrumb(breadcrumb) {
  if (!isFeatureEnabled('enableTelemetry')) return;

  if (SENTRY_DSN && initialized) {
    Sentry.addBreadcrumb({
      category: breadcrumb.category || 'app',
      message: breadcrumb.message,
      level: breadcrumb.level || 'info',
      data: breadcrumb.data,
    });
  }
}

/**
 * Start a performance transaction
 * @param {string} name - Transaction name
 * @param {string} op - Operation type
 * @returns {Object} Transaction object
 */
export function startTransaction(name, op = 'navigation') {
  if (!isFeatureEnabled('enableTelemetry')) return null;

  if (SENTRY_DSN && initialized) {
    return Sentry.startInactiveSpan({ name, op });
  }
  return null;
}

/**
 * Flush pending events (useful before page unload)
 * @param {number} timeout - Timeout in milliseconds
 */
export async function flush(timeout = 2000) {
  if (SENTRY_DSN && initialized) {
    await Sentry.flush(timeout);
  }
}

/**
 * Get the Sentry SDK for advanced usage
 */
export function getSentry() {
  return SENTRY_DSN && initialized ? Sentry : null;
}

export default {
  initTelemetry,
  captureException,
  captureMessage,
  captureBoundaryError,
  captureNetworkError,
  setUser,
  addBreadcrumb,
  startTransaction,
  flush,
  getSentry,
};
